'use client';

import {
	Badge,
	ButtonGroup,
	Card,
	Flex,
	For,
	GridItem,
	HStack,
	LinkBox,
	LinkOverlay,
	Separator,
	SimpleGrid,
	Stack,
	Text,
	useSlotRecipe,
} from '@chakra-ui/react';
import type { Route } from 'next';
import {
	MdBusiness,
	MdCalendarToday,
	MdOutlineLocationOn,
	MdOutlineAccountBalanceWallet,
	MdPersonOutline,
	MdTimelapse,
	MdWorkOutline,
} from 'react-icons/md';

import { AppRoutes } from '@/constants';
import { useAuthStore } from '@/modules/authentication';
import type { JobSearchVM } from '@/modules/job/view-models';
import { NextLinkButton } from '@/modules/navigation/presentation';
import {
	IconLabel, InterestedButton, NextImageWrapper,
} from '@/modules/shared/presentation';

type JobCardProps = {
	job: JobSearchVM

	onClickAddInterestedJob:    () => void
	onClickRemoveInterestedJob: () => void
};

export const JobCard = ({
	job,
	onClickAddInterestedJob,
	onClickRemoveInterestedJob,
}: JobCardProps) => {
	const recipe = useSlotRecipe({ key: 'jobCard' });
	const styles = recipe();
	const { isAuthenticated } = useAuthStore();

	const jobStateDate = job.startDate?.toLocaleDateString();
	const jobEndDate = job.endDate ? ` - ${job.endDate.toLocaleDateString()}` : '';
	const jobDate = `${jobStateDate}${jobEndDate}`;
	const salary = job.salary ? `${job.salary.min} - ${job.salary.max} THB` : 'Not specified';

	const detail = [
		{ icon: MdWorkOutline, key: 'work', label: job.summary },
		{ icon: MdPersonOutline, key: 'person', label: job.eligibility },
		{ icon: MdOutlineLocationOn, key: 'location', label: job.location },
		{ icon: MdOutlineAccountBalanceWallet, key: 'salary', label: salary },
		{ icon:	MdTimelapse, key: 'workHours', label: job.workHours },
		{ icon: MdBusiness, key: 'business', label: job.companyDescription },
	];

	return (
		<LinkBox>
			<Card.Root css={styles.root}>
				<Card.Header css={styles.header}>
					<HStack justifyContent="space-between">
						<Stack gap={0}>
							<LinkOverlay href={job.detailUrl}>
								<Card.Title css={styles.title}>
									{job.companyName}
								</Card.Title>
							</LinkOverlay>
							<Card.Description css={styles.description}>
								{job.title}
							</Card.Description>
						</Stack>
						<NextLinkButton
							colorPalette="primary"
							disabled={job.hasApplied}
							href={isAuthenticated ? job.entryUrl as Route : AppRoutes.auth.login}
						>
							{job.hasApplied ? 'Applied' : 'Entry'}
						</NextLinkButton>
					</HStack>
					<HStack justifyContent="space-between">
						<Flex gap={3}>
							<Badge>New</Badge>
							<Badge variant="outline">Plain</Badge>
						</Flex>
						<IconLabel icon={MdCalendarToday} label={jobDate} variant="caption" />
					</HStack>
				</Card.Header>
				<Separator />
				<Card.Body>
					<SimpleGrid columns={2} gap={4}>
						<GridItem as={Stack} gap="xs">
							<For each={detail}>
								{({ icon, key, label }) => (
									<IconLabel
										gap={4}
										icon={icon}
										iconStyle="round"
										key={key}
										label={label}
										variant="subtle"
									/>
								)}
							</For>
						</GridItem>
						<GridItem>
							<NextImageWrapper
								image={{
									alt: job.coverImage?.caption ?? 'Cover Image',
									src: job.coverImage?.url ?? '/images/default-cover.png',
								}}
								w="100%"
							/>
							<Text
								color="gray.100"
								lineClamp={3}
								mt={4}
								textStyle="caption"
							>
								{job.description}
							</Text>
						</GridItem>
					</SimpleGrid>
				</Card.Body>
				<Card.Footer css={styles.footer}>
					<ButtonGroup css={styles.buttonGroup}>
						<NextLinkButton colorPalette="secondary" href={job.detailUrl as Route}>
							Job Detail
						</NextLinkButton>
						<InterestedButton
							isSelected={job.hasSaved}
							onSelect={onClickAddInterestedJob}
							onUnselect={onClickRemoveInterestedJob}
							selectedLabel="Remove from interested"
							unselectedLabel="Add to interested"
						/>
					</ButtonGroup>
				</Card.Footer>
			</Card.Root>
		</LinkBox>
	);
};
